import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import NotFoundPage from './NotFoundPage';
import LoadingAnimation from './utils/LoadingAnimation';
import { isPartnerHost } from '../partner/paths';
import { initMetaPixel, trackPageView } from '../utils/metaPixel';


const Landing = lazy(() => import('./Landing/Landing'));
const Astrologers = lazy(()=> import('./Astrologer/Astrologers'))
const AstrologerProfile = lazy(()=> import('./Astrologer/AstrologerProfile'))
const ContactUs = lazy(()=>import('../components/ContactUs'))
const CreateAccount = lazy(()=>import('../components/Login/CreateAccount'))
const BookSession = lazy(()=>import('../components/Astrologer/BookSession'))
const PhonePePaymentCallback = lazy(()=>import('../components/Astrologer/PhonePePaymentCallback'))
const BookingSuccessful = lazy(()=>import('../components/HelperJsx/PaymentSuccessPage'))
const PaymentFailed = lazy(()=>import('../components/HelperJsx/PaymentFailedPage'))
const SessionHistory = lazy(()=>import('../components/Astrologer/SessionHistory'))
const MeetingSession = lazy(()=>import('../components/Astrologer/MeetingSession'))
const MyPayments = lazy(()=>import('../components/Help/MyPayments'))
const PaymentDetail = lazy(()=>import('../components/Help/PaymentDetail'))
const BookingDetails = lazy(()=>import('../components/Help/BookingDetails'))
const HelpCenter = lazy(()=>import('../components/Help/HelpCenter'))
const TicketThread = lazy(()=>import('../components/Help/TicketThread'))
const CoupenReedemption = lazy(()=>import('../components/Astrologer/coupenReedemption'))
const AuthenticateFlutrrUser = lazy(()=>import('../components/Authenticate/AutenticateFlutrrUser'))

const PrivacyPolicy = lazy(()=>import('../components/FooterFiles/PrivacyPolicy'))
const TermsAndConditions = lazy(()=>import('../components/FooterFiles/TermsAndConditions'))
const CancellationRefundPolicy = lazy(()=>import('../components/FooterFiles/CancellationPolicy'))
const Blog = lazy(() => import('./Blog/Blog'));
const BlogDetail = lazy(() => import('./Blog/BlogDetail'));
const Seminars = lazy(() => import('./Seminar/Seminars'));
const MediaProfile = lazy(() => import('./Landing/MediaProfile'));
const ServicesPage = lazy(() => import('./Services/ServicesPage'));
const PartnerApp = lazy(() => import('../partner/PartnerApp'));

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Pages holding private links or payment details are not reported to Meta.
const PRIVATE_PATHS = ['/meeting/', '/payments', '/booking/', '/session-history', '/support', '/phonepe-payment-callback'];

const MetaPixelPageViews = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    initMetaPixel();
    if (PRIVATE_PATHS.some((prefix) => pathname.startsWith(prefix))) return;
    trackPageView();
  }, [pathname]);

  return null;
};

const RouteStations = () => {
  // The astrologer dashboard exists ONLY on partner.<domain>, where it is the whole site (no customer routes, header or footer).
  // Every other host is the customer website, which has no dashboard route at all.
  if (isPartnerHost()) {
    return (
      <Router>
        <ScrollToTop />
        <Suspense fallback={<LoadingAnimation />}>
          <Routes>
            <Route path="/*" element={<PartnerApp />} />
          </Routes>
        </Suspense>
      </Router>
    );
  }

  return (
    <Router>
      <ScrollToTop /> {/* Ensure ScrollToTop is placed right inside Router */}
      <MetaPixelPageViews />
      <Navbar />
      <div className="app-container">
        <Suspense fallback={<LoadingAnimation />}>
        <Routes>
                        <Route path="/" element={<Landing />} />
                        <Route path="/book" element={<Astrologers />} />
                        <Route path="/services" element={<ServicesPage />} />
                        <Route path="/blog" element={<Blog />} />
                        <Route path="/blog/:slug" element={<BlogDetail />} />
                        <Route path="/seminar" element={<Seminars />} />
                        <Route path="/media-profile" element={<MediaProfile />} />
                        <Route path="/authenticate" element={<AuthenticateFlutrrUser />} />
                        <Route path="/astrologers/:astroId" element={<AstrologerProfile />} />
                        <Route path="/contactUs" element={<ContactUs />} />
                        <Route path="/Account" element={<CreateAccount/>} />
                        <Route path="/book-session/:astroId/:type" element={<BookSession />} />
                        <Route path="/phonepe-payment-callback" element={<PhonePePaymentCallback />} />
                        <Route path="/session-booked-successful/:astroId" element={<BookingSuccessful />} />
                        <Route path="/payment-failed" element={<PaymentFailed />} />
                        <Route path="/session-history" element={<SessionHistory />} />
                        <Route path="/payments" element={<MyPayments />} />
                        <Route path="/payments/:orderId" element={<PaymentDetail />} />
                        <Route path="/booking/:bookingId" element={<BookingDetails />} />
                        <Route path="/support" element={<HelpCenter />} />
                        <Route path="/support/:ticketId" element={<TicketThread />} />
                        <Route path="/meeting/:meetingUUID" element={<MeetingSession />} />
                        <Route path="/coupen-reedemption/:astroId/:userId" element={<CoupenReedemption />} />
                        <Route path="/privacy-policy" element={<PrivacyPolicy />} />

                        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                        <Route path="/cancellation-refund-policy" element={<CancellationRefundPolicy />} />
                        <Route path="*" element={<NotFoundPage />} />

                    </Routes>
        </Suspense>
      </div>
      <Footer />
    </Router>
  );
};

export default RouteStations;
